import { Avatar, Button, Tooltip, Typography, theme } from "antd";
import { Chip } from "components/ui";
import { Bot, Check, Copy, Headset, User } from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { ChatAuthor } from "types/assistant-handoff.types";
import { BlockRenderer } from "../blocks/BlockRenderer";
import type { ChatBlock } from "../blocks/chat-block.types";
import { stringToBlocks } from "../blocks/toBlocks";
import "../handoff.css";
import { GroundingChip, type GroundingMetadata } from "./GroundingChip";

const { Text } = Typography;

interface ChatMessageProps {
	// `staff` = a person from Equility in a live handoff; `system` = an injected
	// notice (joined / left / ticket opened), drawn as a divider, not a turn.
	role: ChatAuthor;
	content: string;
	// Byline for a staff message.
	staffName?: string;
	// Notice type for a system message (staff_joined, staff_left, ...).
	kind?: string;
	timestamp?: string;
	// Assistant bubble that carries a failure notice (e.g. the data-tool stream
	// timed out) rather than a real answer. Tinted so the failed turn reads as an
	// error instead of a normal response.
	isError?: boolean;
	// Typed content blocks streamed alongside the text (citation/table/…).
	blocks?: ChatBlock[];
	// Grounding signal from the `complete` frame. Rendered as a footer chip so an
	// ungrounded answer is visibly distinct from a grounded one.
	metadata?: GroundingMetadata;
}

/**
 * An injected notice ("Dana from Equility joined the chat"). Not a turn, so no
 * avatar, bubble, or copy button: a centred divider in the flow of the chat.
 */
const SystemNotice: React.FC<{ content: string; kind?: string }> = ({
	content,
	kind,
}) => (
	<div role="note" className="chat-system-notice" data-kind={kind}>
		<span className="chat-system-notice-text">{content}</span>
	</div>
);

/**
 * A reply typed by a person at Equility during a live handoff. ADR 0019-005:
 * it must be impossible to read as the AI, so it carries a name, a staff chip,
 * its own avatar and a tinted bubble, and never the AI grounding chip.
 */
const StaffMessage: React.FC<{
	content: string;
	staffName?: string;
	timestamp?: string;
}> = ({ content, staffName, timestamp }) => {
	const name = staffName?.trim() || "Equility team";
	return (
		<div className="chat-staff-row">
			{/* AntD Avatar takes no role prop, so the accessible image is this
			    wrapper and the avatar itself is decorative. */}
			<span
				role="img"
				aria-label={`${name}, Equility staff`}
				className="chat-avatar-wrap"
			>
				<Avatar
					size={32}
					icon={<Headset size={18} />}
					className="chat-staff-avatar"
					aria-hidden
				/>
			</span>
			<div className="chat-staff-body">
				<div className="chat-staff-byline">
					<span className="chat-staff-name">{name}</span>
					<Chip variant="green">Equility staff</Chip>
				</div>
				{/* Plain text, never markdown: a staff reply is typed by a person,
				    and a rendered link that looks like Equility is a phishing
				    surface. URLs stay visible as text the customer can read. */}
				<p className="chat-staff-bubble chat-staff-text chat-message-enter">
					{content}
				</p>
				{timestamp && (
					<Text type="secondary" className="chat-staff-time">
						{new Date(timestamp).toLocaleTimeString([], {
							hour: "2-digit",
							minute: "2-digit",
						})}
					</Text>
				)}
			</div>
		</div>
	);
};

const ConversationMessage: React.FC<ChatMessageProps> = ({
	role,
	content,
	timestamp,
	isError = false,
	blocks,
	metadata,
}) => {
	const { token } = theme.useToken();
	const [copied, setCopied] = useState(false);
	const isUser = role === "user";
	const isErrorBubble = !isUser && isError;

	const handleCopy = () => {
		navigator.clipboard.writeText(content);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	// Adapt content into typed blocks for rendering. Legacy string responses
	// become a single text block (spec phase 1); future structured responses will
	// arrive as blocks directly, at which point this derivation is skipped.
	const renderedBlocks = [...stringToBlocks(content), ...(blocks ?? [])];

	return (
		<div
			style={{
				display: "flex",
				flexDirection: isUser ? "row-reverse" : "row",
				gap: 12,
				marginBottom: 16,
				alignItems: "flex-start",
				padding: "0 4px",
			}}
		>
			<span
				role="img"
				aria-label={isUser ? "You" : "AI assistant"}
				className="chat-avatar-wrap"
			>
				<Avatar
					size={32}
					aria-hidden
					icon={isUser ? <User size={18} /> : <Bot size={18} />}
					className={isUser ? "chat-avatar-user" : "chat-avatar-ai"}
				/>
			</span>

			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: isUser ? "flex-end" : "flex-start",
					maxWidth: "85%",
				}}
			>
				<div
					className="chat-message-enter"
					style={{
						padding: "10px 14px",
						borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
						background: isErrorBubble
							? token.colorErrorBg
							: isUser
								? token.colorPrimary
								: token.colorBgContainer,
						color: isErrorBubble
							? token.colorErrorText
							: isUser
								? "#fff"
								: token.colorText,
						boxShadow: token.boxShadowTertiary,
						position: "relative",
						border: isErrorBubble
							? `1px solid ${token.colorErrorBorder}`
							: isUser
								? "none"
								: `1px solid ${token.colorBorderSecondary}`,
						width: "100%",
					}}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 8,
							width: "100%",
						}}
					>
						{renderedBlocks.map((block) => (
							<BlockRenderer key={block.id} block={block} isUser={isUser} />
						))}
					</div>
				</div>

				<div
					style={{
						display: "flex",
						gap: 8,
						marginTop: 4,
						opacity: 0.7,
						fontSize: 11,
						alignItems: "center",
					}}
				>
					<Text type="secondary" style={{ fontSize: 11 }}>
						{timestamp
							? new Date(timestamp).toLocaleTimeString([], {
									hour: "2-digit",
									minute: "2-digit",
								})
							: ""}
					</Text>
					{!isUser && !isError && <GroundingChip metadata={metadata} />}
					{!isUser && (
						<Tooltip title={copied ? "Copied!" : "Copy text"}>
							<Button
								type="text"
								size="small"
								icon={copied ? <Check size={12} /> : <Copy size={12} />}
								onClick={handleCopy}
								aria-label="Copy message"
								style={{
									height: 20,
									width: 20,
									minWidth: 20,
									padding: 0,
									color: token.colorTextSecondary,
								}}
							/>
						</Tooltip>
					)}
				</div>
			</div>
		</div>
	);
};

export const ChatMessage: React.FC<ChatMessageProps> = (props) => {
	if (props.role === "system") {
		return <SystemNotice content={props.content} kind={props.kind} />;
	}
	if (props.role === "staff") {
		return (
			<StaffMessage
				content={props.content}
				staffName={props.staffName}
				timestamp={props.timestamp}
			/>
		);
	}
	return <ConversationMessage {...props} />;
};
